import React, { CSSProperties, ReactElement, useState } from 'react'
import Modal, { ModalFooter } from '../../../components/Modal'
import { PrimaryButton, SecondaryButton } from '../../../components/Button'
import Gate from '../../../components/Gate'
import Radom, { Payout } from '../../../state/Radom'
import { errorToast, successToast } from '../../../util/Util'
import { isCancellablePayout } from '../../../util/PayoutStatus'

interface CancelPayoutButtonProps {
  payout: Pick<Payout, 'id' | 'status' | 'confirmedAt'>
  onCancelled: (payout: Payout) => void
  style?: CSSProperties
}

/**
 * Cancels a payout that has not been sent, after asking once. Shown only
 * while the payout can still be cancelled, to roles that may confirm payouts.
 */
const CancelPayoutButton = ({ payout, onCancelled, style }: CancelPayoutButtonProps): ReactElement | null => {
  const [isAsking, setIsAsking] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)

  if (!isCancellablePayout(payout)) {
    return null
  }

  const cancelPayout = async (): Promise<void> => {
    setIsCancelling(true)
    try {
      const cancelled = await Radom.cancelPayout(payout.id)
      successToast('Payout cancelled')
      setIsAsking(false)
      onCancelled(cancelled)
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsCancelling(false)
  }

  return (
    <Gate permission='WriteConfirmPayouts'>
      <SecondaryButton style={style} onClick={() => setIsAsking(true)}>
        Cancel payout
      </SecondaryButton>

      {isAsking &&
        <Modal
          visible={isAsking}
          onClose={() => { if (!isCancelling) setIsAsking(false) }}
          innerStyles={{ width: 420 }}
          title='Cancel this payout?'>
          <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink, #20242d)' }}>
            Nothing has been sent or debited for this payout. Once cancelled, it will not go out on its own.
          </div>

          <ModalFooter $fullWidthAction>
            <SecondaryButton style={{ flex: 1 }} disabled={isCancelling} onClick={() => setIsAsking(false)}>
              Keep payout
            </SecondaryButton>
            <PrimaryButton style={{ flex: 1 }} isLoading={isCancelling} onClick={() => { void cancelPayout() }}>
              Cancel payout
            </PrimaryButton>
          </ModalFooter>
        </Modal>}
    </Gate>
  )
}

export default CancelPayoutButton
